import { useNavigate } from "react-router";
import Icon from "../Icon/Icon";
import styles from "./button.css";
import { Variants } from "./Variants.jsx";

/**
 * A Button represents a fundamental UI element that allows the user to interact
 * with the app and deliberately execute commands. It communicates the action a
 * user can take. Use `InputSubmit` for steps that need to be submitted.
 *
 * @param {Object} props - The properties object.
 * @param {Variants} [props.variant=Variants.PRIMARY] - Determines the visual style of the button.
 * Other options: Variants.SECONDARY, Variants.TERTIARY, Variants.QUATERNARY and Variants.SMALL;
 * Variants are used through the Variants-Object which is used as an enum
 * @param {boolean} [props.disabled=false] - If true, the button will be disabled and non-interactive.
 * @param {function} [props.onClick] - Callback function to handle click events.
 * @param {string} [props.to] - A route to navigate to when clicked.
 * @param {string|null} [props.value=null] - The value attribute of the button.
 * @param {string} [props.width="full"] - Determines the width of the button.
 * @param {string} [props.iconLeft] - The name of the icon to display inside the button on the left.
 * @param {string} [props.iconRight] - The name of the icon to display inside the button on the right.
 * @param {string} [props.type="button"] - The type attribute of the button element.
 * @param {React.ReactNode} props.children - The child elements to be displayed inside the button.
 * @return {JSX.Element} The rendered button element.
 */

function Button({
  variant = Variants.PRIMARY,
  disabled = false,
  onClick,
  to,
  value = null,
  width = "full",
  iconLeft,
  iconRight,
  type = "button",
  children,
}) {
  const navigate = useNavigate();
  function handleClick(e) {
    if (!onClick && !to) return;
    if (!onClick && to) {
      e.preventDefault();
      if (/^[a-z]+:\/\//i.test(to)) {
        // Absolute URL (e.g. another host) — react-router's navigate() would
        // treat it as a relative in-app path and append it to the current URL.
        window.location.href = to;
      } else {
        navigate(to);
      }
    } else if (onClick) {
      e.stopPropagation();
      onClick(e);
    }
  }

  return (
    <button
      type={type}
      value={value}
      className={styles.button({ variant, width })}
      onClick={handleClick}
      disabled={disabled}
      title={value}
    >
      {iconLeft && <Icon name={iconLeft} size="20px" />}

      {children}

      {iconRight && <Icon name={iconRight} size="20px" />}
    </button>
  );
}

export default Button;
